import classNames from 'classnames';
import React, {memo} from 'react';

import {useSetPanelSelection} from '../../state/interactState/hooks';
import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {useIsSingleRun} from '../MultiRunWorkspace/SingleRunMode';
import {useDetectPanelVariant} from '../Panel/hooks/hooks';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {WorkspaceBanners} from '../WorkspaceBanners/WorkspaceBanners';
import {LoaderProxy} from './../../containers/AppLoader';
import {usePanelBankConfigWithRefs} from './hooks/usePanelBankConfigWithRefs';
import {PanelBankControls} from './PanelBankControls';
import {PanelBankInner} from './PanelBankInner';
import {PanelBankProps} from './types';

export const PanelBankLayout = memo(function PanelBankLayout(
  props: PanelBankProps
) {
  const {entityName, projectName, isProjectReadOnly} = props;
  const isSingleRun = useIsSingleRun();

  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const {keyInfoQuery} = useKeyInfoQueryContext();
  const setPanelSelection = useSetPanelSelection();
  const panelBankConfigWithRefs = usePanelBankConfigWithRefs();
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  if (keyInfoQuery.loading || keyInfoQuery.error != null) {
    return <LoaderProxy name="key-info-query-loader" />;
  }

  /**
   * The config provider here is a duplication of the behavior currently in the prop drilling. But the existing drilling doens't go far enough down to toggle run aggregation within the panels. This context avoids extra drilling to support that, and also given the the panelBankConfigWithRefs value is very stable, we can gradually thin out the drilling
   */
  return (
    <div
      className={classNames('panel-bank', {
        'panel-bank-with-runs': !isSingleRun,
        // When entering full-screen mode for a panel, the panel bank remains rendered in the background
        // This prevents unnecessary re-renders when exiting full-screen mode and returning to the workspace
        'full-screen-panel': isFullScreenPanelPage,
      })}>
      <div style={{backgroundColor: 'white'}}>
        <WorkspaceBanners />
        <PanelBankControls
          entityName={entityName}
          panelBankConfigRef={panelBankConfigRef}
          projectName={projectName}
          isProjectReadOnly={isProjectReadOnly}
          workspaceID={props.workspaceID}
        />
      </div>
      <PanelBankInner
        panelBankConfigWithRefs={panelBankConfigWithRefs}
        panelBankProps={props}
        setPanelSelection={setPanelSelection}
      />
    </div>
  );
});
